import React, { useMemo, useState, useEffect } from "react";
import { useLang } from "../../i18n/LanguageContext";
import { translations } from "../../i18n/translations";
import { CATEGORIES } from "../../mock/mock";
import ExpertCard from "./ExpertCard";
import { useReveal } from "../../hooks/useReveal";

const ExpertsSection = ({ experts, onSelect, searchQuery }) => {
  const { lang, t } = useLang();
  const [activeCat, setActiveCat] = useState("all");
  const [ref, inView] = useReveal();

  useEffect(() => { if (searchQuery) setActiveCat("all"); }, [searchQuery]);

  useEffect(() => {
    const handler = (e) => setActiveCat(e.detail || "all");
    window.addEventListener("intro:filter", handler);
    return () => window.removeEventListener("intro:filter", handler);
  }, []);

  const filtered = useMemo(() => {
    let list = experts;
    if (activeCat !== "all") list = list.filter((e) => e.category === activeCat);
    if (searchQuery?.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (e) =>
          e.name.toLowerCase().includes(q) ||
          (e.title[lang] || e.title.en || "").toLowerCase().includes(q) ||
          e.expertise.some((x) => x.toLowerCase().includes(q))
      );
    }
    return list;
  }, [experts, activeCat, searchQuery, lang]);

  const tabs = [{ id: "all", label: t("sections.allExperts") }, ...CATEGORIES.map((c) => ({ id: c.id, label: translations[lang]?.categories?.[c.key] }))];

  return (
    <section id="experts" className="py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div ref={ref} className={`reveal ${inView ? "in-view" : ""}`}>
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="font-display font-extrabold text-3xl sm:text-5xl text-neutral-900 tracking-tight text-balance">
              {t("sections.featured")}
            </h2>
            <p className="mt-3 text-neutral-500 text-lg">{t("sections.featuredSub")}</p>
          </div>

          <div className="mt-9 flex flex-wrap items-center justify-center gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveCat(tab.id)}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
                  activeCat === tab.id
                    ? "bg-neutral-900 text-white shadow-md"
                    : "bg-white text-neutral-600 border border-neutral-200 hover:border-neutral-400"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {filtered.map((e, i) => (
            <div key={e.id} style={{ animationDelay: `${(i % 8) * 60}ms` }} className="animate-scale-in">
              <ExpertCard expert={e} />
            </div>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-20 text-neutral-400">
            <p className="text-lg">🔍 —</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default ExpertsSection;
